import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { CalendarX, X, ArrowRight } from "@phosphor-icons/react";

const DISMISS_KEY = "vs-sub-banner-dismissed-at";
// Re-show every 6h even after dismiss — subscription expiry is high urgency.
const DISMISS_HOURS = 6;

/**
 * Top-of-content subscription expiry banner.
 *
 * Behaviour per role:
 *   - trust_admin / hostel_admin / school_admin → shows when their trust is
 *     within 30 days of expiry, OR already expired/disabled.
 *   - super_admin → shows when ANY trust is expiring soon (aggregate count).
 *   - donor / parent → never shown.
 *
 * Dismissable for 6h via localStorage (key: vs-sub-banner-dismissed-at).
 * Expired banner is NOT dismissable (it's blocking-urgent).
 */
export default function SubscriptionExpiryBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const last = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (last && Date.now() - last < DISMISS_HOURS * 3600 * 1000) {
      setDismissed(true);
    }
    if (user && !["donor", "parent"].includes(user.role)) {
      api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => setSub(null));
    }
  }, [user]);

  if (!user || !sub) return null;
  if (["donor", "parent"].includes(user.role)) return null;
  if (sub.scoped === false) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setDismissed(true);
  };

  // Super-admin aggregate banner
  if (user.role === "super_admin") {
    const total = (sub.trusts_warning_count || 0) + (sub.trusts_expired_count || 0) + (sub.trusts_disabled_count || 0);
    if (total === 0 || dismissed) return null;
    const cta = () => navigate("/app/subscriptions");
    const isUrgent = (sub.trusts_expired_count || 0) > 0 || (sub.trusts_disabled_count || 0) > 0;
    return (
      <div
        className={`mb-4 rounded-lg border-l-4 p-4 flex items-start gap-3 ${
          isUrgent ? "border-red-500 bg-red-50 text-red-900" : "border-amber-500 bg-amber-50 text-amber-900"
        }`}
        data-testid="subscription-banner-super"
        role="alert"
      >
        <CalendarX size={22} weight="bold" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-sm">
            {sub.trusts_expired_count > 0 && `${sub.trusts_expired_count} trust subscription${sub.trusts_expired_count > 1 ? "s" : ""} expired`}
            {sub.trusts_expired_count > 0 && sub.trusts_warning_count > 0 && " · "}
            {sub.trusts_warning_count > 0 && `${sub.trusts_warning_count} expiring within ${sub.warning_threshold_days} days`}
            {sub.trusts_disabled_count > 0 && (sub.trusts_expired_count > 0 || sub.trusts_warning_count > 0) && " · "}
            {sub.trusts_disabled_count > 0 && `${sub.trusts_disabled_count} disabled`}
          </div>
          <div className="text-xs mt-1 leading-relaxed">
            Manage trust subscriptions to keep them active.
          </div>
          <button
            onClick={cta}
            className={`mt-2.5 text-xs font-semibold uppercase tracking-widest font-mono px-3 py-1.5 rounded-md transition-colors inline-flex items-center gap-1 ${
              isUrgent ? "bg-red-900 text-red-50 hover:bg-red-800" : "bg-amber-900 text-amber-50 hover:bg-amber-800"
            }`}
            data-testid="subscription-banner-cta-super"
          >
            Manage subscriptions <ArrowRight size={12} />
          </button>
        </div>
        <button
          onClick={handleDismiss}
          className="text-current/60 hover:text-current p-1"
          aria-label="Dismiss"
          data-testid="subscription-banner-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // Tenant-scoped banner (trust_admin / hostel_admin / school_admin)
  if (!sub.should_warn && !sub.has_expired) return null;
  if (!sub.has_expired && dismissed) return null;

  const days = sub.days_until_expiry;
  const isExpired = sub.has_expired;

  return (
    <div
      className={`mb-4 rounded-lg border-l-4 p-4 flex items-start gap-3 ${
        isExpired ? "border-red-500 bg-red-50 text-red-900" : "border-amber-500 bg-amber-50 text-amber-900"
      }`}
      data-testid="subscription-banner-tenant"
      role="alert"
    >
      <CalendarX size={22} weight="bold" className="flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold text-sm">
          {isExpired
            ? `Subscription expired${sub.paid_until ? ` on ${sub.paid_until}` : ""} — please renew to keep workspace active.`
            : `Subscription expires in ${days} day${days === 1 ? "" : "s"} (${sub.paid_until}).`}
        </div>
        <div className="text-xs mt-1 leading-relaxed">
          {sub.plan_name ? `Current plan: ${sub.plan_name}. ` : ""}
          {isExpired
            ? "Some features may be restricted. Contact your super-admin to renew the trust subscription."
            : `Contact your super-admin to renew before the deadline.`}
        </div>
      </div>
      {!isExpired && (
        <button
          onClick={handleDismiss}
          className="text-current/60 hover:text-current p-1"
          aria-label="Dismiss"
          data-testid="subscription-banner-dismiss-tenant"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
